// shipNames.ts — Deterministic ship designations, derived from the ship id so they're
// stable without needing the server to assign them. A designation is a type prefix
// ("MV" = merchant vessel) plus a proper name, e.g. "MV Donbas".
import type { Ship } from '../state/economy';

const PREFIX: Record<string, string> = {
  freighter: 'MV', // merchant vessel
};

const NAMES = [
  'Donbas', 'Kestrel', 'Meridian', 'Aldebaran', 'Calypso', 'Orinoco', 'Halcyon',
  'Perdido', 'Tarkus', 'Castellan', 'Vagrant', 'Sundancer', 'Mistral', 'Cygnet',
  'Marauder', 'Ondine', 'Bellerophon', 'Argosy', 'Vesper', 'Lodestar', 'Cormorant',
  'Selene', 'Tycho', 'Nimbus', 'Peregrine', 'Solitaire', 'Anvil', 'Drover', 'Bishop',
  'Talos', 'Wayfarer', 'Osprey', 'Galleon', 'Corsair', 'Magellan', 'Drake', 'Hesperus',
  'Antares', 'Borealis', 'Equinox', 'Fathom', 'Gravitas', 'Harrow', 'Ironwood',
  'Juniper', 'Karelia', 'Loricate', 'Manticore', 'Nomad', 'Polaris',
];

const ROMAN = ['', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

// Stable index from the ship id ("ship-12" -> 12; falls back to a char hash).
function shipIndex(id: string): number {
  const m = id.match(/(\d+)/);
  if (m) return parseInt(m[1], 10);
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Type label for a ship kind, e.g. "Freighter". */
export function shipType(kind: string): string {
  return kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : 'Vessel';
}

/** Full designation, e.g. "MV Donbas" (with a numeral suffix once names wrap). */
export function shipName(ship: Pick<Ship, 'id' | 'kind'> & { name?: string }): string {
  if (ship.name) return ship.name; // empire ships carry their own designation
  const prefix = PREFIX[ship.kind] ?? 'SS';
  const n = shipIndex(ship.id);
  const base = NAMES[n % NAMES.length];
  const wrap = Math.floor(n / NAMES.length);
  return wrap > 0 ? `${prefix} ${base} ${ROMAN[wrap] ?? wrap + 1}` : `${prefix} ${base}`;
}
